import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";

import { INTERACTION } from "../login-utils/flowInteractions";
import { primaryButtonSx } from "./LoginChrome";
import type { ByosScreenProps } from "../login-utils/types";

type EnterEmailProps = ByosScreenProps & {
	showLegalCopy?: boolean;
};

function getEmailFromForm(form: Record<string, unknown>): string {
	const v = form.loginId ?? form.email;
	return typeof v === "string" ? v : "";
}

export default function EnterEmail({
	onNext,
	form,
	onFormUpdate,
	errorText,
	isSubmitting = false,
	showLegalCopy = false
}: EnterEmailProps) {
	const [email, setEmail] = useState(getEmailFromForm(form));
	const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

	const handleContinue = async () => {
		if (!isValid) return;
		onFormUpdate({ loginId: email, email });
		await onNext(INTERACTION.EMAIL_ENTERED, { loginId: email, email });
	};

	return (
		<Stack sx={{ gap: 3, mt: 1 }}>
			<Box>
				<Typography
					sx={{
						fontSize: 20,
						fontWeight: 600,
						color: "rgba(13, 31, 48, 0.92)",
						textAlign: "center",
						letterSpacing: "-0.01em",
						mb: 0.5
					}}
				>
					Sign in to CoApp
				</Typography>
			</Box>

			<Stack sx={{ gap: 1.25 }}>
				<TextField
					label="Work email"
					type="email"
					value={email}
					onChange={(e) => setEmail(e.target.value)}
					onKeyDown={(e) => { if (e.key === "Enter") void handleContinue(); }}
					placeholder="you@company.com"
					autoFocus
					fullWidth
					autoComplete="email"
					inputMode="email"
					slotProps={{ inputLabel: { shrink: true } }}
					sx={{
						"& .MuiOutlinedInput-root": {
							borderRadius: 2,
							background: "#FFFFFF",
							"& fieldset": {
								borderColor: "rgba(13, 31, 48, 0.14)",
								transition: "border-color 0.18s"
							},
							"&:hover fieldset": { borderColor: "rgba(2, 213, 171, 0.50)" },
							"&.Mui-focused fieldset": { borderColor: "#02D5AB", borderWidth: 1.5 }
						},
						"& .MuiInputLabel-root": {
							color: "rgba(13, 31, 48, 0.55)",
							"&.Mui-focused": { color: "#057A6D" }
						}
					}}
				/>
			</Stack>

			{errorText && (
				<Typography sx={{ fontSize: 13, color: "#C04A4A", textAlign: "center" }}>
					{errorText}
				</Typography>
			)}

			<Button
				onClick={() => void handleContinue()}
				disabled={!isValid || isSubmitting}
				sx={primaryButtonSx}
				fullWidth
			>
				{isSubmitting ? "Sending link…" : "Continue"}
			</Button>

			{showLegalCopy && (
				<Typography
					sx={{
						fontSize: 12,
						color: "rgba(13, 31, 48, 0.45)",
						textAlign: "center",
						lineHeight: 1.5,
						"& a": {
							color: "var(--coapp-text-interactive, #057A6D)",
							textDecoration: "none",
							fontWeight: 500,
							"&:hover": { textDecoration: "underline", textUnderlineOffset: 2 }
						}
					}}
				>
					By continuing, you agree to CoApp's{" "}
					<a href="https://coapp.io/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
					{" "}and{" "}
					<a href="https://coapp.io/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>.
				</Typography>
			)}
		</Stack>
	);
}
